/**
 * The Command Center trend (mockup 01 "Twelve weeks", blueprint §7 C28/C9):
 * one line per Guardian Score component over the 90-day window. The daily
 * rows come from composeScoreHistory (cc-history.ts). Pure.
 *
 * The rows are daily. With a week or more of history they are sampled
 * weekly, ending on the newest day, so the chart reads like the mockup's
 * weekly lines; a shorter history keeps every day and the tile says how
 * short it is. Values round to whole points, nulls stay gaps, and a
 * component with fewer than two plotted points draws no line.
 */
import type { Category, TrendSeries } from '@/components/carbon'
import type { ScoreDataPoint } from '@/types/executiveReport'
import { dayLabel } from './cc-score'

/** The chart key's names (mockup 01): the pillar pages' names, and Secure Score named as Microsoft's. */
export const TREND_LABEL: Record<Category, string> = {
  baseline: 'Baseline Compliance',
  bpa: 'Best Practice',
  identity: 'Identity',
  secure: 'Microsoft Secure Score',
}

/** The window behind the trend tile and the pillar deltas (§7 C9: "in 90 days"). */
export const HISTORY_DAYS = 90

/** Fewer days than this is a short history: no weekly sampling, and a note on the tile. */
export const SHORT_HISTORY_DAYS = 8

export interface PillarTrend {
  series: TrendSeries[]
  /** The x labels ("Jul 4") in order. */
  xDomain: string[]
  /** Y-axis range with headroom, rounded to tens; undefined when there is no data. */
  yDomain: [number, number] | undefined
  /** "Jul 4 to Oct 2", the first to the last plotted point (so it matches the axis), or null with none. */
  range: string | null
  /** Days with any component value. */
  days: number
  /** True when the series are weekly samples. */
  weekly: boolean
}

const FIELDS: ReadonlyArray<[Category, keyof ScoreDataPoint]> = [
  ['baseline', 'baselineCompliance'],
  ['bpa', 'bpaScore'],
  ['identity', 'mfaAdoption'],
  ['secure', 'secureScore'],
]

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)

export function pillarTrend(points: ReadonlyArray<ScoreDataPoint> | null | undefined): PillarTrend {
  const days = (points ?? [])
    .filter((p) => dayLabel(p.date) != null && FIELDS.some(([, f]) => num(p[f]) != null))
    .slice()
    .sort((a, b) => a.date.localeCompare(b.date))

  const weekly = days.length >= SHORT_HISTORY_DAYS
  const sampled = weekly ? days.filter((_, i) => (days.length - 1 - i) % 7 === 0) : days
  const xDomain = sampled.map((p) => dayLabel(p.date) as string)

  // A line needs two plotted points; a component with fewer is too short to draw and stays off the chart.
  const series: TrendSeries[] = FIELDS.map(([category, field]) => ({
    id: category,
    label: TREND_LABEL[category],
    category,
    points: sampled.map((p, i) => {
      const v = num(p[field])
      return { x: xDomain[i], y: v == null ? null : Math.round(v) }
    }),
  })).filter((s) => s.points.filter((p) => p.y != null).length >= 2)

  const ys = series.flatMap((s) => s.points.map((p) => p.y)).filter((y): y is number => y != null)
  const yDomain: [number, number] | undefined = ys.length
    ? [Math.max(0, Math.floor((Math.min(...ys) - 5) / 10) * 10), Math.min(100, Math.ceil((Math.max(...ys) + 5) / 10) * 10)]
    : undefined

  const range = xDomain.length ? (xDomain.length === 1 ? xDomain[0] : `${xDomain[0]} to ${xDomain[xDomain.length - 1]}`) : null

  return { series, xDomain, yDomain, range, days: days.length, weekly }
}

/** "A", "A and B", "A, B and C". */
function listNames(names: string[]): string {
  return names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/**
 * The tile's subtitle, stating the span it shows: "Weekly score per pillar, Jul 10 to Oct 2", plus where any line
 * starts later than the axis ("; Identity and Microsoft Secure Score start Sep 25"), so a short line never reads
 * as the full range. Null with no data.
 */
export function trendSubtitle(t: PillarTrend): string | null {
  if (!t.range) return null
  const head = `${t.weekly ? 'Weekly' : 'Daily'} score per pillar, ${t.range}`
  const late = new Map<string, string[]>()
  for (const s of t.series) {
    const start = s.points.find((p) => p.y != null)?.x
    if (start != null && start !== t.xDomain[0]) late.set(String(start), [...(late.get(String(start)) ?? []), s.label])
  }
  if (late.size === 0) return head
  const clauses = [...late].map(([start, names]) => `${listNames(names)} ${names.length === 1 ? 'starts' : 'start'} ${start}`)
  return `${head}; ${clauses.join(', ')}`
}
